import React, { startTransition, useRef, useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Card, CardMedia, Typography } from '@mui/material';
import BiodiversityMap from 'src/resources/images/biodevmap.png';
import areaCovered from 'src/resources/images/site-area.png';
import plantationCovered from 'src/resources/images/planted_area.png';
import treeCovered from 'src/resources/images/tree (2).png';
import { useAppState } from 'src/state-management/state-provider';
import cameraInstalled from 'src/resources/images/site-camera.png';
import audioDevices from 'src/resources/images/site-audio.png';

import {
  Site,
  CarbonSiteAnalysis,
  BiodiversitySiteAnalysis,
  EnvironmentalSiteAnalysis,
} from 'src/api/Api';

import scoreCovered from 'src/resources/images/score.png';
import categoryCovered from 'src/resources/images/category.png';
import { isSuccessBioAnalysis, isSuccessCarbonAnalysis, isSuccessEnvAnalysis, roundToTwoDecimalPlaces } from 'src/util/util';
const SiteList = ({ type }: { type: string }) => {
  const navigate = useNavigate();
  const projectId = useParams<{ id: string }>().id;
  const { state, dispatch } = useAppState();
  const {
    carbonSiteAnalysis,
    projectDetails,
    biodiversitySiteAnalysis,
    environmentalSiteAnalysis,
  } = state.selectedProject;
  const containerRef = useRef<HTMLDivElement>(null);
  const [maxWidth, setMaxWidth] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      const handleResize = () => {
        setMaxWidth(containerRef.current?.clientWidth || 0);
      };

      handleResize(); // Initial calculation

      window.addEventListener('resize', handleResize);

      return () => {
        window.removeEventListener('resize', handleResize);
      };
    }
  }, [containerRef]);

  const handleSelectSite = (site: Site) => {
    startTransition(() => {
      dispatch({
        type: 'SET_SELECTED_SITE',
        payload: site,
      });
      navigate(`/project/${projectId}/${type}/site/${site.site_id}`);
    });
  };

  const forCarbon = (
    <Box sx={{ mt: 5 }} ref={containerRef}>
      <Typography sx={{ fontSize: '24px', fontWeight: 600, color: '#151E15', margin: '5px' }}>
        {'Project Sites'}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          overflowX: 'auto',
          gap: 2,
          p: 1,
          maxWidth: maxWidth,
        }}
      >
        {carbonSiteAnalysis &&
          carbonSiteAnalysis?.filter((s: CarbonSiteAnalysis) => {
            return isSuccessCarbonAnalysis(projectDetails?.sites?.find((site: Site) => site.site_id === s.site_id));
          }).map((site: CarbonSiteAnalysis, index: number) => {
            const siteDetails: Site = projectDetails?.sites?.find(
              (siteDetail: Site) => siteDetail.site_id === site.site_id,
            ) as Site;

            return (
              <Card
                key={index}
                className="card-hover"
                sx={{
                  display: 'flex',
                  backgroundColor: '#151E15',
                  borderRadius: 2,
                  overflow: 'hidden',
                  minWidth: 300,
                  maxWidth: 350,
                }}
                onClick={() => handleSelectSite(siteDetails)}
              >
                <Box sx={{ flex: 1 }}>
                  <CardMedia
                    component="img"
                    sx={{ height: '100%', objectFit: 'cover' }}
                    image={BiodiversityMap}
                    alt="Site image"
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 2,
                    gap: 1.5,
                    flex: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#fff' }}>
                    {siteDetails.site_name}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#fff' }}>
                      <img
                        src={areaCovered}
                        alt="area covered"
                        style={{ width: 20, height: 20, color: '#' }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography component="div" sx={{ fontSize: '12px', fontWeight: 500 }}>
                          {'Area Covered'}
                        </Typography>
                        <Typography component="div" sx={{ fontSize: '18px', fontWeight: 600 }}>
                          {roundToTwoDecimalPlaces(siteDetails?.area)}  {projectDetails?.area_unit}
                        </Typography>
                      </Box>
                    </Box>

                    {site.plantation_area && <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#fff' }}>
                      <img
                        src={plantationCovered}
                        alt="camera installed"
                        style={{ width: 20, height: 20 }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography component="div" sx={{ fontSize: '12px', fontWeight: 500 }}>
                          {'Total Plantation Area'}
                        </Typography>
                        <Typography component="div" sx={{ fontSize: '18px', fontWeight: 600 }}>
                          {roundToTwoDecimalPlaces(site.plantation_area)} {site.plantation_area_unit}
                        </Typography>
                      </Box>
                    </Box>
                    }

                    {site.planted_trees && <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#fff' }}>
                      <img
                        src={treeCovered}
                        alt="audio devices"
                        style={{ width: 20, height: 20 }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography component="div" sx={{ fontSize: '12px', fontWeight: 500 }}>
                          {'Total Planted Trees'}
                        </Typography>
                        <Typography component="div" sx={{ fontSize: '18px', fontWeight: 600 }}>
                          {site.planted_trees}
                        </Typography>
                      </Box>
                    </Box>
                    }
                  </Box>
                </Box>
              </Card>
            );
          })}
      </Box>
    </Box>
  );

  const forBio = (
    <Box ref={containerRef}>
      <Typography sx={{ fontSize: '24px', fontWeight: 600, color: '#151E15', margin: '5px' }}>
        {'Project Sites'}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          p: 1,
          overflowX: 'auto',
          maxWidth: maxWidth,
        }}
      >
        {projectDetails &&
          projectDetails?.sites?.filter(isSuccessBioAnalysis).map((site: Site, index: number) => {
            return (
              <Card
                key={index}
                onClick={() => handleSelectSite(site)}
                sx={{
                  display: 'flex',
                  backgroundColor: '#151E15',
                  borderRadius: 2,
                  minWidth: 300,
                  maxWidth: 350,
                }}
              >
                <Box sx={{ flex: 1 }}>
                  <CardMedia
                    component="img"
                    sx={{ height: '100%', objectFit: 'cover' }}
                    image={BiodiversityMap}
                    alt="Site image"
                  />
                </Box>

                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 2,
                    gap: 1.5,
                    flex: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#fff' }}>
                    {site.site_name}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#fff' }}>
                      <img
                        src={areaCovered}
                        alt="area covered"
                        style={{ width: 20, height: 20 }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography component="div" sx={{ fontSize: '12px', fontWeight: 500 }}>
                          {'Area Covered'}
                        </Typography>
                        <Typography component="div" sx={{ fontSize: '18px', fontWeight: 600 }}>
                          {roundToTwoDecimalPlaces(site.area)} {projectDetails?.area_unit}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#fff' }}>
                      <img
                        src={cameraInstalled}
                        alt="camera installed"
                        style={{ width: 20, height: 20 }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography component="div" sx={{ fontSize: '12px', fontWeight: 500 }}>
                          {'Cameras Installed'}
                        </Typography>
                        <Typography component="div" sx={{ fontSize: '18px', fontWeight: 600 }}>
                          {
                            biodiversitySiteAnalysis?.find(
                              (siteData: BiodiversitySiteAnalysis) =>
                                siteData.site_id === site.site_id,
                            )?.camera_installed
                          }
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#fff' }}>
                      <img
                        src={audioDevices}
                        alt="audio devices"
                        style={{ width: 20, height: 20 }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography component="div" sx={{ fontSize: '12px', fontWeight: 500 }}>
                          {'Audio Devices'}
                        </Typography>
                        <Typography component="div" sx={{ fontSize: '18px', fontWeight: 600 }}>
                          {
                            biodiversitySiteAnalysis?.find(
                              (siteData: BiodiversitySiteAnalysis) =>
                                siteData.site_id === site.site_id,
                            )?.audio_devices
                          }
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Card>
            );
          })}
      </Box>
    </Box>
  );

  const forenvironment = (
    <Box ref={containerRef}>
      <Typography sx={{ fontSize: '24px', fontWeight: 600, color: '#151E15', margin: '5px' }}>
        {'Project Sites'}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          p: 1,
          overflowX: 'auto',
          maxWidth: maxWidth,
        }}
      >
        {environmentalSiteAnalysis &&
          environmentalSiteAnalysis?.filter((s: EnvironmentalSiteAnalysis) => {
            return isSuccessEnvAnalysis(projectDetails?.sites?.find((site: Site) => site.site_id === s.site_id));
          }).map((site: EnvironmentalSiteAnalysis, index: number) => {
            const siteDetails: Site = projectDetails?.sites?.find(
              (siteDetail: Site) => siteDetail.site_id === site.site_id,
            ) as Site;
            return (
              <Card
                key={index}
                className="card-hover"
                sx={{
                  display: 'flex',
                  backgroundColor: '#151E15',
                  borderRadius: 2,
                  overflow: 'hidden',
                  minWidth: 300,
                  maxWidth: 350,
                }}
                onClick={() => handleSelectSite(siteDetails)}
              >
                <Box sx={{ flex: 1 }}>
                  <CardMedia
                    component="img"
                    sx={{ height: '100%', objectFit: 'cover' }}
                    image={BiodiversityMap}
                    alt="Site image"
                  />
                </Box>
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    padding: 2,
                    gap: 1.5,
                    flex: 1,
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '18px', color: '#fff' }}>
                    {siteDetails?.site_name}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#fff' }}>
                      <img
                        src={areaCovered}
                        alt="area covered"
                        style={{ width: 20, height: 20 }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography component="div" sx={{ fontSize: '12px', fontWeight: 500 }}>
                          {'Area Covered'}
                        </Typography>
                        <Typography component="div" sx={{ fontSize: '18px', fontWeight: 600 }}>
                          {roundToTwoDecimalPlaces(siteDetails?.area)} {projectDetails?.area_unit}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#fff' }}>
                      <img
                        src={scoreCovered}
                        alt="camera installed"
                        style={{ width: 20, height: 20 }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography component="div" sx={{ fontSize: '12px', fontWeight: 500 }}>
                          {'Site Risk Score'}
                        </Typography>
                        <Typography component="div" sx={{ fontSize: '18px', fontWeight: 600 }}>
                          {roundToTwoDecimalPlaces(site?.site_risk_score)}
                        </Typography>
                      </Box>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, color: '#fff' }}>
                      <img
                        src={categoryCovered}
                        alt="audio devices"
                        style={{ width: 20, height: 20 }}
                      />
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography component="div" sx={{ fontSize: '12px', fontWeight: 500 }}>
                          {'Site Risk Category'}
                        </Typography>
                        <Typography component="div" sx={{ fontSize: '18px', fontWeight: 600 }}>
                          {site?.site_risk_category}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              </Card>
            );
          })}
      </Box>
    </Box>
  );


  if (type === 'carbon') {
    return forCarbon;
  }
  if (type === 'bio-diversity') {
    return forBio;
  }

  if (type === 'climate' || type === 'environment') {
    return forenvironment;
  }
  return null;
};

export default SiteList;
